import React from 'react';

import {
  Checkbox,
  TextInput,
  useMantineTheme,
  Text,
  Tooltip,
  ActionIcon,
  px,
  Button,
  Space,
  Title,
  Paper,
  createStyles,
  Group,
} from '@mantine/core';
import { IconFilterOff, IconSearch, IconX, IconChevronLeft } from '@tabler/icons-react';
import { useNavigate, useParams } from 'react-router-dom';

const useStyles = createStyles(theme => ({
  header: {
    padding: theme.spacing.lg,
    background: theme.colors.gray[0],
    borderRadius: 0,
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
    display: 'grid',
    gridTemplateColumns: 'auto max-content 300px min-content min-content min-content',
    gridTemplateRows: 'auto',
    gridTemplateAreas: `"title lastName search clear button"`,
    alignItems: 'center',
    gap: theme.spacing.sm,
    [theme.fn.smallerThan('md')]: {
      gridTemplateColumns: 'auto auto min-content min-content min-content',
      gridTemplateRows: 'auto auto',
      gridTemplateAreas: `
        'title title button button'
        'lastName search search clear'
      `,
    },
    [theme.fn.smallerThan('sm')]: {
      gridTemplateColumns: 'auto min-content min-content min-content',
      gridTemplateRows: 'auto auto auto',
      gridTemplateAreas: `
        'title button button button'
        'search search search search'
        'lastName lastName clear button'
      `,
    },
  },
  title: {
    gridArea: 'title',
  },
  lastName: {
    gridArea: 'lastName',
    justifySelf: 'end',
    [theme.fn.smallerThan('sm')]: {
      justifySelf: 'start',
    },
  },
  search: {
    gridArea: 'search',
  },
  clear: {
    gridArea: 'clear',
  },
  button: {
    gridArea: 'button',
  },
}));

interface FilterTerms {
  searchTerm: string;
  isArrangeByLastName: boolean;
  loginEnabled: string;
}
const AdminStaffPageHeader = ({
  searchFilters,
  initialSearchFiltersValue,
  setSearchFilters,
}: {
  searchFilters: FilterTerms;
  initialSearchFiltersValue: FilterTerms;
  setSearchFilters: React.Dispatch<React.SetStateAction<FilterTerms>>;
}): JSX.Element => {
  const theme = useMantineTheme();
  const { classes } = useStyles();
  const { gymId } = useParams();
  const navigate = useNavigate();
  const handleBackClick = (): void => {
    navigate(-1);
  };
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.currentTarget?.value || '';
    setSearchFilters(prevValue => ({ ...prevValue, searchTerm: value }));
  };
  const handleIsArrangeByLastNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = !!e.currentTarget?.checked;
    setSearchFilters(prevValue => ({ ...prevValue, isArrangeByLastName: value }));
  };

  const handleClearFilters = (): void => {
    setSearchFilters(initialSearchFiltersValue);
  };
  const handleClearSearchFilter = (): void => {
    setSearchFilters(prevValue => ({ ...prevValue, searchTerm: '' }));
  };
  const handleViewAthletes = (): void => {
    window.location.href = `/gyms/${gymId}/athletes`;
  };

  return (
    <Paper className={classes.header}>
      <Group>
        <ActionIcon onClick={handleBackClick}>
          <IconChevronLeft size={theme.fontSizes.xl} />
        </ActionIcon>
        <Title order={1} size='h1' pr='xl' className={classes.title}>
          Staff
        </Title>
      </Group>

      <Checkbox
        className={classes.lastName}
        checked={searchFilters.isArrangeByLastName}
        onChange={handleIsArrangeByLastNameChange}
        label={
          <Text color='gray' fs='italic'>
            Arrange by Last Name
          </Text>
        }
      />
      <TextInput
        className={classes.search}
        icon={<IconSearch size={theme.fontSizes.md} />}
        placeholder='Search by name, email, phone'
        value={searchFilters?.searchTerm}
        onChange={handleSearchChange}
        rightSection={
          !!searchFilters.searchTerm ? (
            <ActionIcon onClick={handleClearSearchFilter} size='md'>
              <IconX size={theme.fontSizes.md} />
            </ActionIcon>
          ) : (
            <Space />
          )
        }
      />
      <Tooltip label='Clear filters' offset={px(theme.spacing.md)} className={classes.clear}>
        <ActionIcon onClick={handleClearFilters}>
          <IconFilterOff />
        </ActionIcon>
      </Tooltip>

      <Button onClick={handleViewAthletes} className={classes.button}>
        View Athletes
      </Button>
    </Paper>
  );
};

export default AdminStaffPageHeader;
